import React from "react";
import { useTheme } from "../themes/ThemeProvider";
import { THEME_LIST } from "../themes/registry";
import type { ThemeId } from "../themes/types";
import "./ThemeSwitcher.css";

/**
 * Fixed pill that names the current theme and expands to the full list.
 *
 * It rests compact — cycle glyph plus current theme name — because naming the
 * state is what makes it read as a control rather than decoration. With a
 * random default this is the only way a visitor discovers the other four, so it
 * also pulses once on a first ever visit.
 */
const ThemeSwitcher: React.FC = () => {
  const { themeId, theme, setTheme, showPulse, dismissPulse } = useTheme();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => setOpen(false), []);

  // Dismiss on outside click and on Escape so touch users are not trapped open.
  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const choose = (id: ThemeId) => {
    setTheme(id);
    dismissPulse();
    close();
  };

  const toggle = () => {
    setOpen((prev) => !prev);
    dismissPulse();
  };

  return (
    <div
      ref={rootRef}
      className={`theme-switcher${open ? " theme-switcher--open" : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
    >
      <div className="theme-switcher__menu" role="menu" aria-label="Themes">
        {THEME_LIST.map((entry) => {
          const active = entry.id === themeId;
          return (
            <button
              key={entry.id}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className={`theme-switcher__option${
                active ? " theme-switcher__option--active" : ""
              }`}
              onClick={() => choose(entry.id)}
              tabIndex={open ? 0 : -1}
            >
              <span
                className="theme-switcher__dot"
                style={{ background: entry.accent }}
                aria-hidden="true"
              />
              {entry.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={`theme-switcher__trigger${
          showPulse ? " theme-switcher__trigger--pulse" : ""
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Theme: ${theme.label}. Change theme.`}
        onClick={toggle}
      >
        <span className="theme-switcher__glyph" aria-hidden="true">
          ↻
        </span>
        <span className="theme-switcher__label">{theme.label}</span>
      </button>
    </div>
  );
};

export default ThemeSwitcher;
